import React, { useRef, useReducer } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import classNames from "classnames";
import Downshift from "downshift";

import UIInput from "components/UI/Input";
import UIList from "components/UI/List";
import { Calendar } from "components/FormField/Date/Calendar/Calendar";
import { Container as DropdownContainer } from "components/UI/Dropdown";
import IconChevron from "components/Icon/Chevron";

const Container = styled(DropdownContainer).attrs({
  className: "date-dropdown"
})`
  position: relative;

  && .menu {
    overflow-x: unset;
  }

  && .list {
    max-height: 260px;
    overflow-y: unset;
  }

  & .relative {
    position: relative;
  }

  & .calendars {
    position: absolute;
    left: ${props => `${props.calendarPosOffset}px`};
    top: 0;
    height: 260px;
    width: 640px;
    display: flex;
  }
  & .calendars .calendar {
    width: 50%;
    height: 100%;
  }

  & .range-option {
    align-items: center;
    display: flex;
    height: 100%;
    padding: 0 8px 0 0;
    flex: 1;
  }
  & .range-option span {
    flex: 1;
  }
  & .range-option .chevron {
    transform: rotate(-90deg);
  }
`;

const dateOptions = [
  { value: "today", text: "Today" },
  { value: "month_to_date", text: "Month to Date" },
  { value: "year_to_date", text: "Year to Date" },
  { value: "prev_month", text: "Previous Month" }
];

let dates = {
  start_date: new Date(),
  end_date: new Date()
}

const getDateRangeToString = ({ startDate, endDate }) =>{
  return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
}

const stateReducer = (_, changes) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      return changes.selectedItem.value === "range"
        ? { ...changes, isOpen: true }
        : changes;
    default:
      return changes;
  }
};

const reducer = (state, action) => {
  const today = new Date();
  switch (action.type) {
    case "month_to_date":
      return {
        ...state,
        startDate: new Date(today.getFullYear(), today.getMonth(), 1)
      };
    case "year_to_date":
      return { ...state, startDate: new Date(today.getFullYear(), 0, 1) };
    case "prev_month":
      return {
        ...state,
        startDate: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        endDate: new Date(today.getFullYear(), today.getMonth(), 0)
      };
    case "today":
      return { ...state, startDate: today, endDate: today };
    case "start_date":
      return { ...state, startDate: action.payload };
    case "end_date":
      return { ...state, endDate: action.payload };
    case "cal_pos_offset":
      return { ...state, calendarPosOffset: action.payload };
    case "calendars_open":
      return { ...state, calendarsOpen: action.payload };
    default:
      return state;
  }
};

const DateDropdown = ({ className, side, onChange }) => {
  const inputNode = useRef(null);
  const listNode = useRef(null);
  const [state, dispatch] = useReducer(reducer, {
    calendarPosOffset: 0,
    calendarsOpen: false,
    startDate: new Date(),
    endDate: new Date()
  });

  const rangeOption = {
    value: "range",
    text: "Range",
    content: (
      <div className="range-option">
        <span>Range</span> <IconChevron className="chevron" />
      </div>
    )
  };
  const options = [...dateOptions, rangeOption];

  const handleStateChange = changes => {
    if (changes.hasOwnProperty("isOpen")) {
      if (changes.isOpen) {
        inputNode.current && inputNode.current.focus();
      } else {
        dispatch({ type: "calendars_open", payload: false });
      }
    }

    if (
      !state.calendarsOpen &&
      changes.highlightedIndex === options.length - 1
    ) {
      if (listNode.current) {
        const { width } = listNode.current.getBoundingClientRect();
        const offset = side === "right" ? width : -640;
        dispatch({ type: "cal_pos_offset", payload: offset });
      }
      dispatch({ type: "calendars_open", payload: true });

      // const escapeListener = event => {
      //   if (event.code !== "Escape") return;
      //   dispatch({ type: "calendars_open", payload: false });
      //   document.removeEventListener("keyup", escapeListener);
      // };
      // document.addEventListener("keyup", escapeListener);
    }
  };

  const handleChange = selectedItem => {
    dispatch({ type: selectedItem.value });
    const today = new Date();
    switch (selectedItem.value) {
      case "month_to_date":
        dates.start_date = new Date(today.getFullYear(), today.getMonth(), 1);
        dates.end_date = today;
        break;
      case "year_to_date":
        dates.start_date = new Date(today.getFullYear(), 0, 1);
        dates.end_date = today;
        break;
      case "prev_month":
        dates.start_date = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        dates.end_date = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "today":
        dates.start_date = today;
        dates.end_date = today;
        break;
    }
    onChange(dates);
  };

  const handleChangeCalendar = (type, value) => {
    dispatch({ type: type, payload: value });
    switch (type) {
      case "start_date":
        dates.start_date = value;
        break;
      case "end_date":
        dates.end_date = value;
        break;
    }
    onChange(dates);
  }

  return (
    <Downshift
      onChange={handleChange}
      stateReducer={stateReducer}
      defaultHighlightedIndex={0}
      onStateChange={handleStateChange}
      itemToString={item => (item ? item.text : "")}
    >
      {({
        isOpen,
        innerRef,
        toggleMenu,
        getRootProps,
        getMenuProps,
        getItemProps,
        getInputProps,
        highlightedIndex
      }) => (
        <Container
          innerRef={innerRef}
          calendarPosOffset={state.calendarPosOffset}
          {...getRootProps({
            refKey: "innerRef",
            className: classNames(className, { open: isOpen })
          })}
        >
          <UIInput
            className={`dropdown__input ${className}`}
            iconRight={<IconChevron className="chevron" />}
            containerProps={{
              onClick: toggleMenu
            }}
            {...getInputProps({
              ref: inputNode,
              readOnly: true,
              value: getDateRangeToString(state)
            })}
          />

          <div {...getMenuProps({ className: "menu" })}>
            <div className="relative">
              <UIList innerRef={listNode}>
                {isOpen
                  ? options.map((option, idx) => (
                      <UIList.Item
                        key={option.value}
                        {...getItemProps({
                          item: option,
                          className: classNames({
                            active: highlightedIndex === idx
                          })
                        })}
                      >
                        {option.content || option.text}
                      </UIList.Item>
                    ))
                  : null}
              </UIList>
              {state.calendarsOpen ? (
                <div className="calendars">
                  <div className="calendar relative">
                    <Calendar
                      value={state.startDate}
                      onChange={value => handleChangeCalendar("start_date", value)}
                    />
                  </div>
                  <div className="calendar relative">
                    <Calendar
                      value={state.endDate}
                      onChange={value => handleChangeCalendar("end_date", value)}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Container>
      )}
    </Downshift>
  );
};

DateDropdown.propTypes = {
  side: PropTypes.oneOf(["left", "right"])
};
DateDropdown.defaultProps = {
  side: "right"
};

export default DateDropdown;
